import * as React from 'react';

import {
  Button,
  Col,
  Empty,
  IconButton,
  Loading,
  Row,
  TextField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideSearch } from 'lucide-react';

import { LoadCreate } from '../../loads/LoadCreate';

import { LoadListItem } from './LoadListItem';

import {
  $loadsTabContext,
  $loadsTabSoLineItems,
  $remoteLoads,
  $selectedLoadId,
} from '~/modules/trade/_state/loadsTab.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useDebouncedValue } from '~/shared/hooks/useDebounce';

const entityLabelByType = {
  booking: 'booking',
  salesOrder: 'sales order',
  purchaseOrder: 'purchase order',
};

type LoadsSidebarProps = {
  disableAddLoad?: boolean;
};

// ⚛️ ⚛️ ⚛️ LOADS SIDEBAR ⚛️ ⚛️ ⚛️
export const LoadsSidebar: React.FC<LoadsSidebarProps> = ({
  disableAddLoad = false,
}) => {
  const t = useTranslations();

  // States
  const [searchTerm, setSearchTerm] = React.useState('');
  const [isLoadDialogOpen, setIsLoadDialogOpen] = React.useState(false);

  // Atoms
  const context = useAtomValue($loadsTabContext);
  const { data, isLoading, fetch: fetchLoads } = useAtomValue($remoteLoads);
  const { data: soLineItemsData, fetch: fetchSoLineItems } =
    useAtomValue($loadsTabSoLineItems);
  const selectedLoadId = useAtomValue($selectedLoadId);

  const setSelectedLoadId = useSetAtom($selectedLoadId);

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);

  // Memoized values
  const enrichedLoads = React.useMemo(() => {
    if (!data?.loads) return [];
    return data.loads.map((load) => {
      if (!load.salesOrderId || !soLineItemsData) return load;
      const soItemIds = soLineItemsData[load.salesOrderId] ?? [];
      const combinedItemIds = new Set([...load.materialItemIds, ...soItemIds]);
      return {
        ...load,
        lineItemCount: combinedItemIds.size,
      };
    });
  }, [data?.loads, soLineItemsData]);

  const filteredLoads = React.useMemo(() => {
    if (enrichedLoads.length === 0) return [];
    if (!debouncedSearchTerm) return enrichedLoads;
    return enrichedLoads.filter((load) =>
      load.loadNumber
        ?.toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase()),
    );
  }, [enrichedLoads, debouncedSearchTerm]);

  // Callbacks
  const handleClose = React.useCallback(() => {
    setIsLoadDialogOpen(false);
  }, []);

  // Effects
  /** Fetch loads when context changes */
  React.useEffect(() => {
    if (!context) return;
    fetchLoads({
      ...(context.entityType === 'booking' && { bookingId: context.bookingId }),
      ...(context.entityType === 'salesOrder' && {
        salesOrderId: context.salesOrderId,
      }),
      ...(context.entityType === 'purchaseOrder' && {
        purchaseOrderId: context.purchaseOrderId,
      }),
    });
  }, [fetchLoads, context]);

  /** Fetch SO line items for enrichment */
  React.useEffect(() => {
    if (!data?.loads) return;
    const salesOrderIds = [
      ...new Set(
        data.loads
          .map((load) => load.salesOrderId)
          .filter((id): id is string => !!id),
      ),
    ];
    if (salesOrderIds.length > 0) {
      fetchSoLineItems({ salesOrderIds });
    }
  }, [data?.loads, fetchSoLineItems]);

  if (isLoading && !data) {
    return (
      <Row className="w-64 p-4" xCenter yCenter>
        <Loading />
      </Row>
    );
  }

  if (data?.loads.length === 0) {
    return (
      <div className="h-full w-full">
        <Empty
          className="h-full"
          title={t.loadDetail.loadsTab.emptyState.title}
          description={t.loadDetail.loadsTab.emptyState.subtitle(
            entityLabelByType[context?.entityType ?? 'salesOrder'],
          )}
          action={
            !disableAddLoad ? (
              <Button
                variant="solid"
                color="primary"
                icon={<LucidePlusCircle className="size-5" />}
                onPress={() => setIsLoadDialogOpen(true)}
              >
                {t.loadDetail.loadsTab.emptyState.createButton}
              </Button>
            ) : undefined
          }
          centered={true}
        />
        <LoadCreate
          isOpen={isLoadDialogOpen}
          requiredBookingId={context?.bookingId}
          requiredSalesOrderId={context?.salesOrderId}
          onClose={handleClose}
        />
      </div>
    );
  }

  return (
    <Col gap2 className="@LoadsSidebar w-64 overflow-y-auto">
      <Row gap2>
        <TextField
          placeholder={t.loadDetail.loadsTab.search.placeholder}
          value={searchTerm}
          onChange={setSearchTerm}
          leftAdornment={<LucideSearch size={18} className="text-neutral/60" />}
          className="h-10 w-52 bg-white"
        />
        {!disableAddLoad && (
          <IconButton
            variant="solid"
            color="primary"
            className="size-10 w-10"
            icon={<LucidePlusCircle size={18} />}
            onPress={() => setIsLoadDialogOpen(true)}
          />
        )}
      </Row>

      <Col gap2 className="overflow-y-auto">
        {filteredLoads.map((load) => (
          <LoadListItem
            key={load.id}
            load={load}
            isSelected={selectedLoadId === load.id}
            showBorder
            onClick={() => setSelectedLoadId(load.id ?? null)}
          />
        ))}
      </Col>

      <LoadCreate
        isOpen={isLoadDialogOpen}
        requiredBookingId={context?.bookingId}
        requiredSalesOrderId={context?.salesOrderId}
        onClose={handleClose}
      />
    </Col>
  );
};
